import React from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { AlertProvider } from '@/contexts/AlertContext'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Login } from '@/pages/auth/Login'
import { Register } from '@/pages/auth/Register'

// Admin pages
import { AdminDashboard } from '@/pages/admin/AdminDashboard'
import { AdminAcademic } from '@/pages/admin/AdminAcademic'
import { AdminProfile } from '@/pages/admin/AdminProfile'
import { AdminScheduleStatus } from '@/pages/admin/AdminScheduleStatus'
import { AdminTeachers } from '@/pages/admin/AdminTeachers'
import { AdminSessions } from '@/pages/admin/AdminSessions'

// Teacher pages
import { TeacherDashboard } from '@/pages/teacher/TeacherDashboard'
import { TeacherScoreEntry } from '@/pages/teacher/TeacherScoreEntry'
import { TeacherSubjects } from '@/pages/teacher/TeacherSubjects'
import { TeacherClassrooms } from '@/pages/teacher/TeacherClassrooms'
import { TeacherProfile } from '@/pages/teacher/TeacherProfile'
import { ScoringGrid } from '@/pages/teacher/ScoringGrid'
import { TeacherClassroomsList } from '@/pages/teacher/TeacherClassroomsList'
import { ExamRepository } from '@/pages/teacher/ExamRepository'
import { ExamCalendar } from '@/pages/teacher/ExamCalendar'

// HOD pages
import { DepartmentDashboard } from '@/pages/department/DepartmentDashboard'
import { DepartmentClassrooms } from '@/pages/department/DepartmentClassrooms'
import { DepartmentReports } from '@/pages/department/DepartmentReports'
import { DepartmentProfile } from '@/pages/department/DepartmentProfile'

// Helper to redirect '/' to the correct home based on role
const HomeRedirect: React.FC = () => {
  const { role } = useAuth()
  if (role === 'admin') return <Navigate to="/admin" replace />
  if (role === 'head_of_department') return <Navigate to="/department" replace />
  return <Navigate to="/teacher" replace />
}

export const App: React.FC = () => {
  return (
    <AuthProvider>
      <AlertProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Auth Path */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

          {/* Protected Dashboard Paths */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            {/* Root Route Redirect */}
            <Route index element={<HomeRedirect />} />

            {/* Admin Routes */}
            <Route
              path="admin"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/academic"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminAcademic />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/profile"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/schedule-status"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminScheduleStatus />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/teachers"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminTeachers />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/sessions"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminSessions />
                </ProtectedRoute>
              }
            />

            {/* Teacher Routes */}
            <Route
              path="teacher"
              element={
                <ProtectedRoute allowedRoles={['teacher', 'head_of_department', 'admin']}>
                  <TeacherDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="teacher/entry"
              element={
                <ProtectedRoute allowedRoles={['teacher', 'head_of_department', 'admin']}>
                  <TeacherScoreEntry />
                </ProtectedRoute>
              }
            />
            <Route
              path="teacher/subjects"
              element={
                <ProtectedRoute allowedRoles={['teacher', 'head_of_department', 'admin']}>
                  <TeacherSubjects />
                </ProtectedRoute>
              }
            />
            <Route
              path="teacher/classrooms"
              element={
                <ProtectedRoute allowedRoles={['teacher', 'head_of_department', 'admin']}>
                  <TeacherClassrooms />
                </ProtectedRoute>
              }
            />
            <Route
              path="teacher/classrooms-list"
              element={
                <ProtectedRoute allowedRoles={['teacher', 'head_of_department', 'admin']}>
                  <TeacherClassroomsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="teacher/profile"
              element={
                <ProtectedRoute allowedRoles={['teacher', 'head_of_department', 'admin']}>
                  <TeacherProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="teacher/score/:assignmentId"
              element={
                <ProtectedRoute allowedRoles={['teacher', 'head_of_department', 'admin']}>
                  <ScoringGrid />
                </ProtectedRoute>
              }
            />
            <Route
              path="teacher/exam-repository"
              element={
                <ProtectedRoute allowedRoles={['teacher', 'head_of_department', 'admin']}>
                  <ExamRepository />
                </ProtectedRoute>
              }
            />
            <Route
              path="teacher/calendar"
              element={
                <ProtectedRoute allowedRoles={['teacher', 'head_of_department', 'admin']}>
                  <ExamCalendar />
                </ProtectedRoute>
              }
            />

            {/* Head of Department Routes */}
            <Route
              path="department"
              element={
                <ProtectedRoute allowedRoles={['head_of_department', 'admin']}>
                  <DepartmentDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="department/classrooms"
              element={
                <ProtectedRoute allowedRoles={['head_of_department', 'admin']}>
                  <DepartmentClassrooms />
                </ProtectedRoute>
              }
            />
            <Route
              path="department/reports"
              element={
                <ProtectedRoute allowedRoles={['head_of_department', 'admin']}>
                  <DepartmentReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="department/profile"
              element={
                <ProtectedRoute allowedRoles={['head_of_department']}>
                  <DepartmentProfile />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Catch-all Redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </AlertProvider>
    </AuthProvider>
  )
}

export default App
